import apicaller from "@/utils/apicaller";
import api from "../main.api";

const pollConfig = {
    pollInterval: 5000,
    maxPollAttempts: 120
};

export default {
    configure(config) {
        if (config && config.pollInterval) {
            pollConfig.pollInterval = config.pollInterval;
        }
        if (config && config.maxPollAttempts) {
            pollConfig.maxPollAttempts = config.maxPollAttempts;
        }
    },
    getPollConfig() {
        return { pollInterval: pollConfig.pollInterval, maxPollAttempts: pollConfig.maxPollAttempts };
    },
    runExecute(payload) {
        return apicaller.post(api.CFA_ANALYSIS.execute, payload);
    },
    runIncomeAnalysis(payload) {
        return apicaller.post(api.CFA_ANALYSIS.incomeAnalysis, payload);
    },
    runAmlScreening(payload) {
        return apicaller.post(api.CFA_ANALYSIS.amlScreening, payload);
    },
    runFraudScreening(payload) {
        return apicaller.post(api.CFA_ANALYSIS.fraudScreening, payload);
    },
    runContactAnalysis(payload) {
        return apicaller.post(api.CFA_ANALYSIS.contactAnalysis, payload);
    },
    runProfileSummary(payload) {
        return apicaller.post(api.CFA_ANALYSIS.profileSummary, payload);
    },
    getAnalysis(analysisId) {
        return apicaller.get(`${api.CFA_ANALYSIS.analysis}/${analysisId}`);
    },
    getProfileSummary(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/profile-summary`, payload);
    },
    getRegularIncome(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/regular-income`, payload);
    },
    getMoneyFlow(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/money-flow`, payload);
    },
    getAmountDistribution(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/amount-distribution`, payload);
    },
    getContactNetwork(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/contact-network`, payload);
    },
    getAnomalies(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/anomalies`, payload);
    },
    getTemporalBehavior(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/temporal-behavior`, payload);
    },
    getVelocity(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/velocity`, payload);
    },
    getStructuring(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/structuring`, payload);
    },
    getDormancy(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/dormancy`, payload);
    },
    getLayering(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/layering`, payload);
    },
    getBehavioralDeviation(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/behavioral-deviation`, payload);
    },
    getCashTransactions(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/cash-transactions`, payload);
    },
    getMultiHopPaths(analysisId, payload) {
        return apicaller.post(`${api.CFA_ANALYSIS.analysis}/${analysisId}/multi-hop-paths`, payload);
    },
    findConnection(payload) {
        return apicaller.post(api.CFA_ANALYSIS.findConnection, payload);
    },
    getHistory(payload) {
        return apicaller.post(api.CFA_ANALYSIS.history, payload);
    },
    deleteAnalysis(analysisId) {
        return apicaller.delete(`${api.CFA_ANALYSIS.analysis}/${analysisId}`);
    },
    pollAnalysis(analysisId) {
        let attempts = 0;
        const poll = (resolve, reject) => {
            if (attempts >= pollConfig.maxPollAttempts) {
                console.warn("CFA_AUDIT", `[POLL] Timeout after ${attempts} attempts (${Math.round(attempts * pollConfig.pollInterval / 1000)}s), analysisId=${analysisId}`);
                reject(new Error("Analysis polling timeout"));
                return;
            }
            attempts++;
            apicaller.get(`${api.CFA_ANALYSIS.analysis}/${analysisId}`)
                .then((response) => {
                    const body = (response.data && response.data.result) || response.data || {};
                    const status = body.status;

                    if (typeof body.progressPercent === "number" && poll._onProgress) {
                        poll._onProgress(body.progressPercent);
                    }
                    poll._retryCount = 0;

                    if (status === 2 || status === 4) {
                        console.warn("CFA_AUDIT", `[POLL] Complete - status=${status}, attempts=${attempts}, analysisId=${analysisId}`);
                        resolve(response);
                    } else if (status === 3) {
                        console.warn("CFA_AUDIT", `[POLL] Failed - status=3, error=${body.errorMessage}, analysisId=${analysisId}`);
                        reject(new Error(body.errorMessage || "Analysis failed"));
                    } else if (status === 5) {
                        console.warn("CFA_AUDIT", `[POLL] Cancelled - status=5, analysisId=${analysisId}`);
                        reject(new Error("Analysis cancelled"));
                    } else {
                        if (attempts % 5 === 0) {
                            console.warn("CFA_AUDIT", `[POLL] Pending - status=${status}, attempt=${attempts}, progress=${body.progressPercent || "N/A"}%, analysisId=${analysisId}`);
                        }
                        const pollTimeout = window.setTimeout(() => {
                            poll(resolve, reject);
                        }, pollConfig.pollInterval);
                        if (!poll._timeouts) {
                            poll._timeouts = [];
                        }
                        poll._timeouts.push(pollTimeout);
                    }
                })
                .catch((error) => {
                    const isNetworkError = !error.response;
                    if (!poll._retryCount) {
                        poll._retryCount = 0;
                    }
                    if (isNetworkError && poll._retryCount < 3) {
                        poll._retryCount++;
                        console.warn("CFA_AUDIT", `[POLL] Network error, retry ${poll._retryCount}/3 - ${error.message}, analysisId=${analysisId}`);
                        const retryTimeout = window.setTimeout(() => {
                            poll(resolve, reject);
                        }, pollConfig.pollInterval);
                        if (!poll._timeouts) {
                            poll._timeouts = [];
                        }
                        poll._timeouts.push(retryTimeout);
                    } else {
                        console.warn("CFA_AUDIT", `[POLL] Fatal error - isNetwork=${isNetworkError}, retries=${poll._retryCount}, httpStatus=${error.response && error.response.status}, message=${error.message}`);
                        reject(error);
                    }
                });
        };
        const promise = new Promise(poll);
        promise.cancel = () => {
            if (poll._timeouts) {
                poll._timeouts.forEach((t) => window.clearTimeout(t));
            }
        };
        promise.onProgress = (callback) => {
            poll._onProgress = callback;
            return promise;
        };
        return promise;
    }
};
